import React, { useState } from 'react';
import { QueryClientProvider } from 'react-query';
import { css } from '@emotion/react';
import { useNavigate, useParams } from 'react-router';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import CalendarSVG from '@birdi/icons/calendar.svg';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useMetaSelector } from '../meta/reducer';
import { getRelativeTime } from '../dashboard-new/utils';
import { mapSitesQueryClient, useSite, useToleranceHistory } from './queries';
import { MapsTab } from './MapsTab';
import { ReferenceSetsTab } from './ReferenceSetsTab';
import { TolerancesTab } from './TolerancesTab';
import { SiteMenu } from './SiteMenu';
import { NewMapModal } from './NewMapModal';
import { buttonGreen, countPillCSS, metaRowCSS, tabButtonCSS } from './styles';
import { UpgradeIndicator } from '@birdi/components-common';
import { hasGcp, hasPrism, hasSites } from './site-features';

type SiteTab = 'maps' | 'reference-sets' | 'tolerances';

// Fill the white content card with the app's OWN background colour (theme.sand,
// same as the layout `main`) so it reads as one continuous surface — no visible
// second background. The header and tabs are white panels on top, and the sand
// shows through as the gaps between them and between the content cards.
const pageCSS = (theme: BirdiTheme) => css`
  background: ${theme.sand};
  border-radius: 15px;
  min-height: 100%;
  box-sizing: border-box;
  padding: 0.5em 1em 0.75em;
  /* Tight edges on desktop, matching the dashboard content container. */
  @media (min-width: ${breakpoints.m}) {
    padding: 0.5em 0.5em 0.75em 0.25em;
  }
`;

// White header panel; the margin below it shows the sand gap before the tabs.
const headerPanelCSS = (theme: BirdiTheme) => css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  background: ${theme.mono0};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  margin-bottom: 0.5rem;
`;

// Name + address + last-activity on one line on wide screens; wraps when narrow.
const titleRowCSS = css`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  flex-wrap: wrap;
  h2 {
    margin: 0;
  }
`;

// White tabs panel (rounded top only); small sand gap before the content.
const tabsPanelCSS = (theme: BirdiTheme) => css`
  display: flex;
  background: ${theme.mono0};
  border-radius: 10px 10px 0 0;
  padding: 0 0.75rem;
  margin-bottom: 0.5rem;
`;

const actionsCSS = css`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const SitePageContent = ({ siteId }: { siteId: string }) => {
  const navigate = useNavigate();
  const meta = useMetaSelector();
  // `gcp` gates reference sets as a whole; `prism` gates only the prism set
  // within the tab — and, the same way, the tolerance tab, which configures
  // limits for a prism network the org can't upload without it.
  const canUseReferenceSets = hasGcp(meta.features);
  const canUsePrisms = hasPrism(meta.features);
  const [tab, setTab] = useState<SiteTab>('maps');
  // "Create map" from a site creates a new map that's added to this site, with
  // an optional "bring across" selection of reference sets / prior DEMs. No
  // "add to existing" chooser — an existing map wouldn't attach here (and may
  // already belong to another site).
  const [createMapOpen, setCreateMapOpen] = useState(false);

  const { data: site, isLoading } = useSite(siteId);
  // Always fetched (not gated on the tab being open) because the count sits on
  // the tab button itself — but not when the org has no prism entitlement,
  // where the pill is replaced by the upgrade badge and the request would buy
  // nothing.
  const { data: toleranceHistory } = useToleranceHistory(siteId, canUsePrisms);
  const toleranceVersionCount = toleranceHistory?.length ?? 0;

  if (isLoading) {
    return (
      <div
        css={css`
          padding: 2em;
          text-align: center;
        `}
      >
        Loading ...
      </div>
    );
  }

  if (!site) {
    return (
      <p
        css={css`
          margin: 2em;
        `}
      >
        Site not found.
      </p>
    );
  }

  return (
    <div css={pageCSS}>
      <header css={headerPanelCSS}>
        <div css={titleRowCSS}>
          <h2>{site.name}</h2>
          <div css={metaRowCSS}>
            <MapPinSVG />
            <span>{site.address || '—'}</span>
          </div>
          <div css={metaRowCSS}>
            <CalendarSVG />
            <span>
              Last activity{' '}
              {site.lastActivityAt ? getRelativeTime(site.lastActivityAt) : '—'}
            </span>
          </div>
        </div>
        <div css={actionsCSS}>
          {/* Opens the New map modal (create-a-new-map added to this site, with
              an optional "bring across" selection). Site cover photos are
              changed from the ⋯ menu. */}
          <button
            type="button"
            css={buttonGreen}
            data-testid="site-create-map-button"
            onClick={() => setCreateMapOpen(true)}
          >
            Create map
          </button>
          <SiteMenu site={site} onArchived={() => navigate('/sites')} />
        </div>
      </header>

      <div css={tabsPanelCSS}>
        <button
          type="button"
          css={(theme) => tabButtonCSS(theme, tab === 'maps')}
          data-testid="site-tab-maps"
          onClick={() => setTab('maps')}
        >
          Maps <span css={countPillCSS}>{site.mapsCount}</span>
        </button>
        {/* The tab is ALWAYS shown. Without `gcp-processing` it carries an
            upgrade badge and the panels inside are dimmed — the customer should
            see what the plan would give them, not an absence. */}
        <button
          type="button"
          css={(theme) => tabButtonCSS(theme, tab === 'reference-sets')}
          data-testid="site-tab-reference-sets"
          onClick={() => setTab('reference-sets')}
        >
          Reference sets{' '}
          {canUseReferenceSets ? (
            <span css={countPillCSS}>{site.referenceSetsCount}</span>
          ) : (
            <UpgradeIndicator message="Upgrade plan to use reference sets" />
          )}
        </button>
        {/* Shown to everyone, badged and dimmed without `prism-processing` —
            the same treatment the prism reference set gets, so a plan that
            can't hold a prism network sees what it would gain rather than an
            absence. The count is the number of saved versions, and the pill
            is only drawn once there is at least one: a bare "0" on a site
            that has never been configured reads as an unexplained tag rather
            than as an empty list (BIR-373). */}
        <button
          type="button"
          css={(theme) => tabButtonCSS(theme, tab === 'tolerances')}
          data-testid="site-tab-tolerances"
          onClick={() => setTab('tolerances')}
        >
          Tolerance levels{' '}
          {canUsePrisms ? (
            toleranceVersionCount > 0 && (
              <span css={countPillCSS}>{toleranceVersionCount}</span>
            )
          ) : (
            <UpgradeIndicator message="Upgrade plan to use prism networks" />
          )}
        </button>
      </div>

      <div>
        {tab === 'maps' && <MapsTab siteId={siteId} />}
        {tab === 'reference-sets' && <ReferenceSetsTab siteId={siteId} />}
        {tab === 'tolerances' && (
          <TolerancesTab siteId={siteId} locked={!canUsePrisms} />
        )}
      </div>

      {/* Create-a-new-map added to this site, with the optional "bring across"
          selection; on create we land on the new map with the uploader. */}
      <NewMapModal
        open={createMapOpen}
        setOpen={setCreateMapOpen}
        site={{ id: site.id, name: site.name }}
      />
    </div>
  );
};

export const SitePage = () => {
  const meta = useMetaSelector();
  const { id } = useParams<{ id: string }>();

  if (!hasSites(meta.features)) {
    return (
      <p
        css={css`
          margin: 2em;
        `}
      >
        Sites are not enabled for this organisation.
      </p>
    );
  }
  if (!id) return null;

  return (
    <QueryClientProvider client={mapSitesQueryClient}>
      <SitePageContent siteId={id} />
    </QueryClientProvider>
  );
};
